var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { ReflectUtils } from "@dooboostore/core";
const ON_ATTRIBUTE_CHANGED_METADATA_KEY = Symbol.for("simple-web-component:on-attribute-changed");
const applyChangedAttribute = (attributeName, options, target, propertyKey) => {
  const constructor = target.constructor;
  let metaList = ReflectUtils.getOwnMetadata(ON_ATTRIBUTE_CHANGED_METADATA_KEY, constructor);
  if (!metaList) {
    metaList = [];
    ReflectUtils.defineMetadata(ON_ATTRIBUTE_CHANGED_METADATA_KEY, metaList, constructor);
  }
  const name = attributeName || String(propertyKey);
  metaList.push({
    attributeName: name,
    propertyKey,
    options
  });
};
function changedAttribute(attributeNameOrTarget, optionsOrPropertyKey, descriptor) {
  if ((typeof optionsOrPropertyKey === "string" || typeof optionsOrPropertyKey === "symbol") && descriptor !== void 0) {
    applyChangedAttribute(void 0, {}, attributeNameOrTarget, optionsOrPropertyKey);
    return;
  }
  const attributeName = attributeNameOrTarget;
  const options = optionsOrPropertyKey ?? {};
  return (target, propertyKey) => {
    applyChangedAttribute(attributeName, options, target, propertyKey);
  };
}
const getChangedAttributeMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(ON_ATTRIBUTE_CHANGED_METADATA_KEY, constructor).flat();
};
const findAllAttributeChangedMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  const metaList = ReflectUtils.findAllMetadata(ON_ATTRIBUTE_CHANGED_METADATA_KEY, constructor) || [];
  const result = /* @__PURE__ */ new Map();
  metaList.forEach((meta) => {
    meta.forEach((item) => {
      if (!result.has(item.attributeName)) {
        result.set(item.attributeName, []);
      }
      result.get(item.attributeName).push(item);
    });
  });
  return result;
};
const convertAttributeValue = (val, type) => {
  return convertValue(val, type);
};
import { getAttributeValue, convertValue } from "./applyAttribute.js";
import { SwcUtils } from "../utils/Utils.js";
import { buildSwcParameterArgs, buildCommonKindValues } from "./parameter.js";
class ChangedAttributeLifeCycler {
  constructor() {
    __publicField(this, "attrChangeMap", null);
  }
  /** 클래스 메타데이터 기반 attrChangeMap 을 lazy 로드한다 (공유 시클러 캐시). */
  getAttrChangeMap(inst) {
    if (!this.attrChangeMap) this.attrChangeMap = findAllAttributeChangedMetadata(inst);
    return this.attrChangeMap;
  }
  /** observedAttributes 에 포함할 attribute 이름 목록 */
  getObservedAttributeNames(inst) {
    return Array.from(this.getAttrChangeMap(inst).keys());
  }
  onConnected(helperHostSet) {
    const inst = helperHostSet.$this;
    for (const [name, metaList] of this.getAttrChangeMap(inst)) {
      for (const meta of metaList) {
        if (meta.options.while === "connected") {
          const val = getAttributeValue(inst, name, { type: meta.options.type });
          if (val !== null) this.runChanged(helperHostSet, meta, val, null, name);
        }
      }
    }
  }
  /** attributeChangedCallback 에서 elementDefine 이 직접 호출 */
  onAttributeChanged(helperHostSet, name, old, newVal) {
    const inst = helperHostSet.$this;
    const metaList = this.getAttrChangeMap(inst).get(name);
    if (!metaList) return;
    for (const meta of metaList) {
      if (meta.options.while === "connected" && !inst.__swc_connected) continue;
      this.runChanged(helperHostSet, meta, convertAttributeValue(newVal, meta.options.type), old, name);
    }
  }
  // filter(async)/before/finally + @changedAttributeBeforeReturn 주입. fire-and-forget.
  runChanged(helperHostSet, meta, value, old, name) {
    const inst = helperHostSet.$this;
    const opts = meta.options;
    void (async () => {
      const helper = SwcUtils.getHelperAndHostSet(inst);
      if (opts.filter && !await opts.filter(value, { currentThis: inst, helper })) return;
      const common = buildCommonKindValues(inst, helperHostSet.$w);
      const legacyArgs = [value, old, name, helper];
      const buildArgs = (beforeReturn) => buildSwcParameterArgs(inst, meta.propertyKey, {
        ...common,
        changedAttributeBeforeReturn: beforeReturn
      }, [...legacyArgs, beforeReturn]);
      let args = buildArgs(void 0);
      if (opts.before) {
        const br = await opts.before(value, { currentThis: inst, helper });
        args = buildArgs(br);
      }
      let result, error;
      try {
        result = await inst[meta.propertyKey](...args);
      } catch (e) {
        error = e;
      } finally {
        if (opts.finally) await opts.finally(value, { currentThis: inst, helper }, { args, result, error });
      }
      if (error) throw error;
    })().catch((e) => console.error("[SWC] changedAttribute handler error:", e));
  }
}
export {
  ChangedAttributeLifeCycler,
  ON_ATTRIBUTE_CHANGED_METADATA_KEY,
  changedAttribute,
  convertAttributeValue,
  findAllAttributeChangedMetadata,
  getChangedAttributeMetadata
};
//# sourceMappingURL=changedAttribute.js.map
